// Кабинет друзей ходит на api.ttex.online. Вход — по initData (кабинет открыт как Telegram Mini
// App кнопкой из бота); initData шлём на каждом запросе, плюс cookie-сессия как в партнёрском.
export const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

export class ApiError extends Error {
  status: number;
  detail: string;
  constructor(status: number, detail: string) {
    super(detail || `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

async function throwApiError(r: Response): Promise<never> {
  let detail = "";
  try {
    const body = (await r.json()) as { detail?: unknown };
    if (typeof body?.detail === "string") detail = body.detail;
  } catch {
    /* non-JSON */
  }
  throw new ApiError(r.status, detail);
}

export function friendlyError(e: unknown, fallback = "Что-то пошло не так. Попробуйте позже."): string {
  if (!(e instanceof ApiError)) return fallback;
  const d = (e.detail || "").toLowerCase();
  if (d.includes("service_unavailable") || d.includes("quote_unavailable")) return "Сервис временно недоступен. Попробуйте чуть позже.";
  switch (e.status) {
    case 400:
      return e.detail || "Проверьте введённые данные.";
    case 401:
      return "Сессия не активна. Откройте кабинет из бота заново.";
    case 403:
      return "Нет доступа к кабинету друзей.";
    case 404:
      return "Не найдено.";
    case 409:
      return e.detail || "Действие уже выполнено или конфликтует с текущим состоянием.";
    case 413:
      return "Файл слишком большой.";
    default:
      return fallback;
  }
}

function tgInitHeader(): Record<string, string> {
  const d = (window as { Telegram?: { WebApp?: { initData?: string } } }).Telegram?.WebApp?.initData;
  return d ? { "X-Telegram-Init-Data": d } : {};
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 20000);
  const headers: Record<string, string> = { ...tgInitHeader() };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  let r: Response;
  try {
    r = await fetch(API_URL + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: "include",
      signal: ctrl.signal,
    });
  } finally {
    clearTimeout(timer);
  }
  if (!r.ok) await throwApiError(r);
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

const apiGet = <T,>(p: string) => req<T>("GET", p);
const apiPost = <T,>(p: string, b?: unknown) => req<T>("POST", p, b);
const apiPut = <T,>(p: string, b?: unknown) => req<T>("PUT", p, b);

// ── типы ──
export type Host = { user_id: string; phone: string | null; payout_wallet: string | null; friends_count: number };

export type KycLabel = "none" | "draft" | "pending" | "verified" | "rejected";
export type Friend = {
  id: string;
  phone: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  kyc: KycLabel;
  account_rub: number;
  broker: { client_id: number | null; kyc_status: string | null; agreement_status: string | null; signed: boolean } | null;
  created_at: string | null;
};

export type SignContext = {
  session_id: string;
  aggregate_hash: string | null;
  documents: { documentId: string; documentHashRendered: string }[];
};
export type OrderView = {
  id: string;
  reference: string;
  status: string;
  amount_rub: number;
  requisites?: Record<string, unknown> | null;
};
export type WithdrawalView = {
  id: string;
  reference: string;
  status: string;
  amount_rub: number;
  net_usdt: number;
  tx_hash: string | null;
};

export type CreateFriendIn = {
  phone: string;
  email: string;
  first_name?: string;
  last_name?: string;
  city?: string;
  address_line?: string;
  postal_code?: string;
};

export type ProfileIn = {
  first_name: string;
  last_name: string;
  middle_name?: string;
  date_of_birth: string;
  passport_series?: string;
  passport_number: string;
  issued_at?: string;
  issued_by?: string;
  division_code?: string;
  residency_country?: string;
};

export type KycFileKind = "passport_spread" | "selfie_with_passport" | "registration";

export const friendsApi = {
  telegram: (initData: string) => apiPost<Host>("/v1/friends/auth/telegram", { init_data: initData }),
  me: () => apiGet<Host>("/v1/friends/me"),
  logout: () => apiPost<{ ok: boolean }>("/v1/friends/logout"),

  wallet: () => apiGet<{ address: string | null }>("/v1/friends/wallet"),
  setWallet: (address: string) => apiPut<{ address: string }>("/v1/friends/wallet", { address }),

  list: () => apiGet<{ friends: Friend[] }>("/v1/friends/friends"),
  create: (body: CreateFriendIn) => apiPost<Friend>("/v1/friends/friends", body),
  get: (id: string) => apiGet<Friend>(`/v1/friends/friends/${id}`),

  kycProfile: (id: string, body: ProfileIn) => apiPost<unknown>(`/v1/friends/friends/${id}/kyc/profile`, body),
  kycFile: (id: string, kind: KycFileKind, content_type: string, file_base64: string) =>
    apiPost<unknown>(`/v1/friends/friends/${id}/kyc/file`, { kind, content_type, file_base64 }),
  kycSubmit: (id: string) => apiPost<{ submitted: boolean }>(`/v1/friends/friends/${id}/kyc/submit`),

  agreementSendOtp: (id: string) =>
    apiPost<{ already_signed: boolean; otp_id: string; otp_expires_at: string; sign_context: SignContext }>(
      `/v1/friends/friends/${id}/agreement/send-otp`,
    ),
  agreementConfirm: (id: string, sign_context: SignContext, otp_id: string, otp: string) =>
    apiPost<Friend>(`/v1/friends/friends/${id}/agreement/confirm`, { sign_context, otp_id, otp }),
  agreementOtp: (id: string) => apiGet<{ code: string | null }>(`/v1/friends/friends/${id}/agreement/otp`),

  createOrder: (id: string, amount_rub: number) => apiPost<OrderView>(`/v1/friends/friends/${id}/order`, { amount_rub }),
  orders: (id: string) => apiGet<{ orders: OrderView[] }>(`/v1/friends/friends/${id}/orders`),
  withdraw: (id: string, amount_rub: number) => apiPost<WithdrawalView>(`/v1/friends/friends/${id}/withdraw`, { amount_rub }),
  withdrawals: (id: string) => apiGet<{ withdrawals: WithdrawalView[] }>(`/v1/friends/friends/${id}/withdrawals`),
};
